import React, { useMemo, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';

/**
 * Projects / Referenzen (CI-clean)
 * - Kein Endlos-Slider, kein Auto-Scroll, kein "Show"-Motion
 * - Ruhiges Raster, reale Bilder, klare Fakten
 */
export default function ProjectsSection({ mockData }) {
  const projects = useMemo(() => mockData?.projects || [], [mockData]);
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section
      id="projects"
      data-bg="light"
      className="py-20 bg-eos-weißFast text-eos-grauDunkel"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            Referenzen
          </h2>
          <p className="mt-4 text-eos-grauDunkleres">
            Beispiele realer Projekte. Fokus: Struktur, Schnittstellen, Inbetriebnahme.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {projects.map((p) => {
            const hasImages = Array.isArray(p.images) && p.images.length > 0;
            const heroImg = hasImages ? p.images[0] : null;
            const isOpen = openId === p.id;

            return (
              <article
                key={p.id}
                className="border border-eos-sek-blauGrau bg-eos-weißFaster rounded-none overflow-hidden"
              >
                {/* Bild */}
                <div className="relative aspect-[4/3] bg-eos-sek-blauGrau">
                  {heroImg ? (
                    <img
                      src={heroImg}
                      alt={p.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full" />
                  )}
                  {/* ruhige Abgrenzung */}
                  <div className="absolute inset-0 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)]" />
                </div>

                {/* Inhalt */}
                <div className="p-6">
                  <h3 className="text-lg font-semibold leading-snug">
                    {p.title}
                  </h3>

                  {p.location && (
                    <p className="mt-2 text-sm text-eos-grauDunkleres">
                      {p.location}
                    </p>
                  )}

                  {p.description && (
                    <p className="mt-4 text-sm leading-relaxed text-eos-grauDunkleres">
                      {p.description}
                    </p>
                  )}

                  {/* Details */}
                  <div className="mt-6">
                    <Button
                      variant="outline"
                      className="rounded-none border-eos-sek-blauGrau text-eos-grauDunkel hover:bg-eos-weißFast"
                      onClick={() => toggle(p.id)}
                    >
                      {isOpen ? 'Details schließen' : 'Details anzeigen'}
                    </Button>
                  </div>

                  {isOpen && (
                    <div className="mt-6 border-t border-eos-sek-blauGrau pt-6 space-y-4">
                      {Array.isArray(p.highlights) && p.highlights.length > 0 && (
                        <div>
                          <div className="text-sm font-semibold">Schwerpunkte</div>
                          <ul className="mt-2 space-y-2 text-sm text-eos-grauDunkleres">
                            {p.highlights.slice(0, 6).map((h, idx) => (
                              <li key={idx} className="flex gap-2">
                                <span className="mt-[7px] h-1.5 w-1.5 bg-eos-prim-blue" />
                                <span>{h}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {hasImages && p.images.length > 1 && (
                        <div>
                          <div className="text-sm font-semibold">Weitere Bilder</div>
                          <div className="mt-3 grid grid-cols-3 gap-2">
                            {p.images.slice(1, 7).map((src, idx) => (
                              <div key={idx} className="aspect-square bg-eos-sek-blauGrau overflow-hidden">
                                <img
                                  src={src}
                                  alt={`${p.title} – Bild ${idx + 2}`}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-12">
          <Button
            size="lg"
            className="rounded-none bg-eos-prim-blue text-eos-weißFast hover:bg-eos-prim-blauStark"
            onClick={() => {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Projekt besprechen
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}
